import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class H3Polyfill extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.h3polyfill',
        group: 'Spatial Indexes',
        title: 'H3 Polyfill',
        description: `
      This component generates a table containing all the H3 cell indexes with centers contained in a given polygon.
      In case of lines, it will return the H3 cell indexes intersecting those lines. For points, it will
      return the H3 indexes of the cells in which those points are contained.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'resolution',
          type: WorkflowComponentParamType.Number,
          title: 'Resolution',
          helper: 'Hexagon edge length: 2=182km | 6=4km | 10=76m | 14=1.5m',
          min: 0,
          max: 15,
          default: 8,
          mode: WorkflowComponentParameterMode.Slider
        },
        {
          name: 'aggregate',
          type: WorkflowComponentParamType.Boolean,
          title: 'Aggregate H3 cells',
          default: false,
          helper: 'If false, the result will include the original columns from the input source'
        },
        {
          name: 'includegeocolumns',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include geo columns',
          default: false,
          helper: 'If true, the result will include the original geo column(s)',
          showIf: [{ parameter: 'aggregate', value: false }]
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Mode',
          options: ['center', 'intersects', 'contains'],
          default: 'center',
          mode: WorkflowComponentParameterMode.Toggle,
          providers: [Provider.Postgres, Provider.BigQuery]
        },
        {
          name: 'databricks_mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Mode',
          options: ['center', 'intersects'],
          default: 'center',
          mode: WorkflowComponentParameterMode.Toggle,
          providers: [Provider.DatabricksRest]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let source = ''
    if (connection.type === Provider.DatabricksRest) {
      source = `(
                        SELECT /*+ REPARTITION(40) */
                          *
                        FROM ${connection.quoteName(preparedParams.source as string)}
                        WHERE ${preparedParams.geo} IS NOT NULL
                      )`
    } else {
      source = `(
                        SELECT *
                        FROM ${connection.quoteName(preparedParams.source as string)}
                        WHERE ${preparedParams.geo} IS NOT NULL
                      )`
    }
    if (!schemas && !preparedParams.aggregate) {
      return {
        query: '',
        outputs: {}
      }
    }
    let colsToAdd = ''
    const vectorSchema = schemas?.input?.['source']
    if (!vectorSchema && !preparedParams.aggregate) {
      return {
        query: '',
        outputs: {}
      }
    }
    if (!preparedParams.aggregate) {
      let colsFromSchema = vectorSchema as TableSchema
      const typesToExclude = [ColumnDataType.Geography, ColumnDataType.Geometry]
      if (connection.type === Provider.DatabricksRest) {
        typesToExclude.push(ColumnDataType.Binary)
      }
      if (!preparedParams.includegeocolumns) {
        colsFromSchema = colsFromSchema.filter((c) => !typesToExclude.includes(c.type))
      }
      if (colsFromSchema.length > 0) {
        colsToAdd = colsFromSchema.map((c) => `s.${c.name}`).join(', ') + ','
      }
    }
    let select = ''
    switch (connection.type) {
      case Provider.BigQuery:
        if (!preparedParams.aggregate) {
          select = dedent`WITH __h3 AS
                    (
                      SELECT
                        ${colsToAdd}
                        ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL_MODE(
                            ${preparedParams.geo},
                            CAST(${preparedParams.resolution} AS INTEGER),
                            '${preparedParams.mode}'
                          ) h3s
                      FROM
                        ${dedent(source)} s
                    )
                    SELECT * EXCEPT(h3s)
                    FROM __h3, __h3.h3s AS h3
                    `
        } else {
          select = dedent`WITH __h3 AS
                    (
                      SELECT
                        ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL_MODE(
                          ${preparedParams.geo},
                          CAST(${preparedParams.resolution} AS INTEGER),
                          '${preparedParams.mode}'
                        ) h3s
                      FROM
                        ${dedent(source)} s
                    )
                    SELECT DISTINCT h3
                    FROM __h3, __h3.h3s AS h3
                    `
        }
        break
      case Provider.Snowflake:
        if (!preparedParams.aggregate) {
          select = dedent`WITH __h3 AS
                (
                  SELECT
                    ${colsToAdd}
                    CASE WHEN ST_DIMENSION(${preparedParams.geo}) = 0 THEN
                      [${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_FROMGEOGPOINT(
                          ${preparedParams.geo}, CAST(${preparedParams.resolution} AS INTEGER)
                        )]
                    ELSE
                      ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL(
                          ${preparedParams.geo},
                          CAST(${preparedParams.resolution} AS INTEGER)
                        )
                    END h3s
                  FROM
                    ${dedent(source)} s
                )
                SELECT * EXCLUDE(h3s), TO_CHAR(h3_element.value) AS h3
                FROM __h3, lateral FLATTEN(input => h3s) h3_element
                `
        } else {
          select = dedent`WITH __h3 AS
          (
            SELECT
              CASE WHEN ST_DIMENSION(${preparedParams.geo}) = 0 THEN
                [${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_FROMGEOGPOINT(
                    ${preparedParams.geo}, CAST(${preparedParams.resolution} AS INTEGER)
                  )]
              ELSE
                ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL(
                    ${preparedParams.geo},
                    CAST(${preparedParams.resolution} AS INTEGER)
                  )
              END h3s
            FROM
              ${dedent(source)} s
          )
          SELECT TO_CHAR(h3_element.value) AS h3
          FROM __h3, lateral FLATTEN(input => h3s) h3_element
          `
        }
        break
      case Provider.Postgres:
        if (!preparedParams.aggregate) {
          select = dedent`WITH __h3 AS
                (
                  SELECT
                    ${preparedParams.geo},
                      ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL(
                        ${preparedParams.geo},
                        CAST(${preparedParams.resolution} AS INTEGER),
                        '${preparedParams.mode}'
                      ) h3s
                  FROM
                    ${dedent(source)} s
                )
                SELECT
                  ${colsToAdd}
                  (UNNEST(h3s)) AS h3
                FROM
                  ${dedent(source)} s,
                  __h3 b
                WHERE b.${preparedParams.geo} = s.${preparedParams.geo}
                `
        } else {
          select = dedent`WITH __h3 AS
          (
            SELECT
             ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_POLYFILL(
                ${preparedParams.geo},
                CAST(${preparedParams.resolution} AS INTEGER),
                '${preparedParams.mode}'
              ) h3s
            FROM
              ${dedent(source)} s
          )
          SELECT DISTINCT(UNNEST(h3s)) AS h3
          FROM __h3
          `
        }
        break
      case Provider.DatabricksRest: {
        const h3PolyfillFunct =
          preparedParams.databricks_mode === 'center' ? 'H3_POLYFILLASH3STRING' : 'H3_COVERASH3STRING'
        if (!preparedParams.aggregate) {
          select = dedent`
                      SELECT /*+ REPARTITION(40) */
                        ${colsToAdd}
                        EXPLODE(${h3PolyfillFunct}(
                          ${preparedParams.geo},
                          CAST(${preparedParams.resolution} AS INTEGER)
                        )) h3
                      FROM
                        ${dedent(source)} s`
        } else {
          select = dedent`
                        SELECT /*+ REPARTITION(40) */
                          DISTINCT EXPLODE(${h3PolyfillFunct}(
                            ${preparedParams.geo},
                            CAST(${preparedParams.resolution} AS INTEGER)
                          )) h3
                        FROM
                          ${dedent(source)} s
                      `
        }
        break
      }
    }
    let query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    if (connection.type === Provider.Postgres) {
      if (dryRun) {
        select = dedent`SELECT 'h3' AS h3
                        FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
      } else {
        query = dedent`IF EXISTS(SELECT 1 FROM pg_extension WHERE extname = 'plv8') THEN
                  ${dedent(query)}
                ELSE
                  RAISE EXCEPTION 'plv8 extension not installed';
                END IF;`
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.2.0'
      case Provider.BigQuery:
        return '1.1.0'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    const vectorSchema = columns.source
    const outputCols = [{ name: 'h3', type: ColumnDataType.String }]
    if (!params.aggregate) {
      let colsFromSchema = vectorSchema as TableSchema
      const typesToExclude = [ColumnDataType.Geography, ColumnDataType.Geometry]
      if (connection.type === Provider.DatabricksRest) {
        typesToExclude.push(ColumnDataType.Binary)
      }
      if (!params.includegeocolumns) {
        colsFromSchema = colsFromSchema.filter((c) => !typesToExclude.includes(c.type))
      }
      outputCols.push(...colsFromSchema)
    }
    return outputCols
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.DatabricksRest]
  }
}

export { H3Polyfill }
